import React, { useEffect, useState } from "react";
import { useDeployPublicEndpointMutation, useGetAiWorkerByIdQuery, useEntityResourcePermissions } from "@circuitry-ai/doc-data";
import { RadioGroup, Radio, Spinner, Switch } from "@nextui-org/react";
import { checkType, CtyButton, CtyShowToast } from "@circuitry-ai/doc-ui";
import { useTranslation } from "react-i18next";

type Visibility = '0' | '1';
type DeploymentMetadata = Record<string, unknown> & { showingPal?: boolean };

function deploymentMetadata(value: unknown): DeploymentMetadata {
  if (value && typeof value === 'object') return { ...(value as Record<string, unknown>) };
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? { ...parsed } : {};
  } catch {
    return {};
  }
}

interface DeployAgentTabProps {
  agent: { aiworker_id?: string; is_enabled?: boolean; metadata?: unknown } | null;
  onVisibilityUpdated?: (isPublic: boolean, metadata?: DeploymentMetadata) => void;
}

const DeployAgentTab: React.FC<DeployAgentTabProps> = ({ agent, onVisibilityUpdated }) => {
  const { t } = useTranslation();
  const aiworkerId = agent?.aiworker_id;
  const { canUpdate } = useEntityResourcePermissions('agent', aiworkerId);

  // Source of truth for the current toggle state comes from GET /aiworkers/{id} — the navigation-state
  // agent object can be stale (e.g. updated by another user/tab since it was cached).
  const { data: agentDetails, isFetching: isFetchingAgent, refetch: refetchAgentDetails } = useGetAiWorkerByIdQuery(
    aiworkerId ?? '',
    { skip: !aiworkerId, refetchOnMountOrArgChange: true }
  );

  const isPublic = agentDetails?.is_enabled ?? agent?.is_enabled ?? false;
  const savedMetadata = deploymentMetadata(agentDetails?.metadata ?? agent?.metadata);
  const savedShowingPal = savedMetadata.showingPal === true;
  // '0' = Public, '1' = Private. Defaults to Private when the agent has no saved preference yet.
  const [visibility, setVisibility] = useState<Visibility>(isPublic ? '0' : '1');
  const [showingPal, setShowingPal] = useState(savedShowingPal);

  useEffect(() => {
    setVisibility(isPublic ? '0' : '1');
    setShowingPal(savedShowingPal);
  }, [aiworkerId, isPublic, savedShowingPal]);

  const [deployPublicEndpoint, { isLoading }] = useDeployPublicEndpointMutation();

  const handleSaveVisibility = async () => {
    if (!aiworkerId || !canUpdate) {
      return;
    }
    const nextIsPublic = visibility === '0';
    const metadata = {
      ...deploymentMetadata(agentDetails?.metadata ?? agent?.metadata),
      showingPal,
    };
    try {
      const response = await deployPublicEndpoint({
        aiworker_id: aiworkerId,
        is_enabled: nextIsPublic,
        metadata,
      }).unwrap();
      onVisibilityUpdated?.(nextIsPublic, metadata);
      await refetchAgentDetails();
      CtyShowToast(checkType.success, response?.message || t('Deploy settings updated successfully'));
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message || t('Error updating Agent deploy settings'));
    }
  };

  // Every section reads from the same agent fetch, so the whole tab waits on one loader rather
  // than each section showing its own.
  if (isFetchingAgent && !agentDetails) {
    return (
      <div className="flex h-full flex-1 items-center justify-center px-3">
        <Spinner size="lg" label={t('Loading deploy settings...')} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full flex-1 px-3 overflow-y-auto">
      <div className="py-3 sm:flex gap-3">
        <div className="sm:w-[60%]">
          <div className="font-semibold">{t('Visibility')}</div>
          <p className="pt-2 text-base">{t('These settings control who can access the Agent.')}</p>
        </div>
        <div className="sm:w-[100%]">
          <RadioGroup
            className="mt-3"
            value={visibility}
            onValueChange={(value) => setVisibility(value === '0' ? '0' : '1')}
            isDisabled={!canUpdate || isFetchingAgent}
          >
            <Radio value="0" className="items-start">
              <span className="font-medium">{t('Public')}</span>
              <p className="text-sm">{t('Use this option to make your Agent accessible to your customers or the dealers in your dealer network.')}</p>
            </Radio>
            <Radio value="1" className="items-start">
              <span className="font-medium">{t('Private')}</span>
              <p className="text-sm">{t("Makes the Agent accessible for internal use only. This is a good option if you're creating an Agent as a resource for your internal teams.")}</p>
            </Radio>
          </RadioGroup>
        </div>
      </div>
      <div className="py-3 sm:flex gap-3 border-t border-divider">
        <div className="sm:w-[60%]">
          <div className="font-semibold">{t('Showing PAL')}</div>
          <p className="pt-2 text-base">
            {t('Show this Agent in PAL agent selections.')}
          </p>
        </div>
        <div className="sm:w-[100%] flex items-center">
          <Switch
            isSelected={showingPal}
            onValueChange={setShowingPal}
            isDisabled={!canUpdate || isFetchingAgent}
            aria-label={t('Showing PAL')}
          >
            {showingPal ? t('Enabled') : t('Disabled')}
          </Switch>
        </div>
      </div>
      <div className="flex justify-end p-2">
        <CtyButton
          id="agent-visibility-save-btn"
          isLoading={isLoading}
          onPress={() => handleSaveVisibility()}
          color="primary"
          size="sm"
          isDisabled={!canUpdate || isFetchingAgent}
        >
          {t('Save')}
        </CtyButton>
      </div>
    </div>
  );
};

export { DeployAgentTab };
